import { useEffect, useMemo } from 'react'
import { T } from '../strings'
import type { Theme } from '../theme'
import { mix } from '../lib/color'
import {
  BOSS_HP,
  MAX_HEARTS,
  RACE_SECONDS,
  type GameState,
  type Mode,
  type Question,
} from '../game/types'
import type { GameStore } from '../game/store'

interface Props {
  state: GameState
  theme: Theme
  store: GameStore
}

const BOSSES = ['👾', '🐲', '🦑', '🤖', '👹']

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '⌫', '0', '↵'] as const

const FALLBACK: Question = {
  mode: 'choice',
  a: 2,
  b: 2,
  answer: 4,
  opts: [4, 6, 8, 2],
  pairs: [],
  rights: [],
}

function modeLabel(mode: Mode): string {
  switch (mode) {
    case 'choice':
      return T.choose
    case 'keypad':
      return T.type
    case 'match':
      return T.match
    case 'race':
      return T.race
    case 'boss':
      return T.boss
  }
}

export default function Session({ state, theme, store }: Props) {
  const q = state.q ?? FALLBACK
  const showKeypad = q.mode === 'keypad'
  const showOptions = q.mode === 'choice' || q.mode === 'race' || q.mode === 'boss'

  // Физическая клавиатура — приложение живёт и на ноутбуке.
  useEffect(() => {
    if (!showKeypad) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') store.typeDigit(e.key)
      else if (e.key === 'Backspace') store.backspace()
      else if (e.key === 'Enter') store.submitInput()
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [showKeypad, store])

  const hintDots = useMemo(
    () => Array.from({ length: Math.min(24, q.answer) }, (_, i) => i),
    [q.answer],
  )

  const bossFace = BOSSES[state.idx % BOSSES.length]
  const doneValues = state.mDone.map((i) => q.pairs[i]?.r)

  const optionColors = (value: number) =>
    state.fb !== null && value === q.answer
      ? { bg: theme.acc, fg: theme.onacc }
      : { bg: theme.acc2, fg: '#fff' }

  return (
    <div className="session">
      <div className="session__head">
        <button type="button" className="icon-btn" aria-label={T.back} onClick={store.goHome}>
          ‹
        </button>
        <div className="round-dots">
          {state.rounds.map((_, i) => (
            <div
              key={i}
              className="round-dot"
              style={{
                background: i < state.idx ? theme.acc : i === state.idx ? theme.acc2 : theme.line,
              }}
            />
          ))}
        </div>
        <div className="hearts" aria-label={`${state.hearts}/${MAX_HEARTS}`}>
          {Array.from({ length: MAX_HEARTS }, (_, i) => (
            <span key={i} style={{ opacity: i < state.hearts ? 1 : 0.2 }} aria-hidden>
              ❤️
            </span>
          ))}
        </div>
      </div>

      <div className="session__mode">
        <div className="label">{modeLabel(q.mode)}</div>
        {state.combo >= 2 && <div className="combo">×{state.combo} 🔥</div>}
      </div>

      <div className="stage">
        {(q.mode === 'choice' || q.mode === 'keypad') && (
          <div className="equation">
            {q.a} <span style={{ color: theme.acc2 }}>×</span> {q.b}{' '}
            <span style={{ color: theme.ink2 }}>=</span>{' '}
            <span style={{ color: theme.acc }}>
              {q.mode === 'keypad' ? state.input || '?' : '?'}
            </span>
          </div>
        )}

        {q.mode === 'boss' && (
          <div className="stage__stack">
            <div className="boss-art" aria-hidden>
              {bossFace}
            </div>
            <div className="boss-hp">
              <div
                className="bar__fill"
                style={{
                  background: theme.warn,
                  width: `${Math.round((state.bossHp / BOSS_HP) * 100)}%`,
                }}
              />
            </div>
            <div className="equation">
              {q.a} × {q.b}
            </div>
          </div>
        )}

        {q.mode === 'match' && (
          <div className="match-grid">
            <div className="match-col">
              {q.pairs.map((pair, i) => {
                const done = state.mDone.includes(i)
                const selected = state.mLeft === i
                return (
                  <button
                    key={i}
                    type="button"
                    className="match-btn"
                    aria-pressed={selected}
                    disabled={done}
                    onClick={() => store.tapMatchLeft(i)}
                    style={{
                      borderColor: done ? theme.acc : selected ? theme.acc2 : theme.line,
                      background: done ? theme.acc : selected ? mix(theme.acc2, 0.35) : theme.card,
                      color: done ? theme.onacc : theme.ink,
                    }}
                  >
                    {pair.a} × {pair.b}
                  </button>
                )
              })}
            </div>
            <div className="match-col">
              {q.rights.map((value, i) => {
                const done = doneValues.includes(value)
                return (
                  <button
                    key={`${value}-${i}`}
                    type="button"
                    className="match-btn"
                    disabled={done}
                    onClick={() => store.tapMatchRight(value)}
                    style={{
                      borderColor: done ? theme.acc : theme.line,
                      background: done ? theme.acc : theme.card,
                      color: done ? theme.onacc : theme.ink,
                    }}
                  >
                    {value}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {q.mode === 'race' && (
          <div className="stage__stack">
            <div className="race-bar">
              <div
                className="race-bar__fill"
                style={{
                  background: theme.warn,
                  width: `${Math.round((state.raceLeft / RACE_SECONDS) * 100)}%`,
                }}
              />
            </div>
            <div className="race-score">
              {T.solved} {state.raceScore}
            </div>
            <div className="equation">
              {q.a} × {q.b}
            </div>
          </div>
        )}
      </div>

      <div className="pad">
        {showOptions && (
          <div className="options-grid">
            {q.opts.map((value, i) => {
              const colors = optionColors(value)
              return (
                <button
                  key={`${value}-${i}`}
                  type="button"
                  className="btn-3d option-btn"
                  disabled={state.locked}
                  onClick={() => store.grade(value)}
                  style={{ background: colors.bg, color: colors.fg }}
                >
                  {value}
                </button>
              )
            })}
          </div>
        )}

        {showKeypad && (
          <div className="keypad">
            {KEYS.map((label) => {
              const isEnter = label === '↵'
              const isBack = label === '⌫'
              return (
                <button
                  key={label}
                  type="button"
                  className="btn-3d key"
                  disabled={state.locked}
                  onClick={() => {
                    if (isBack) store.backspace()
                    else if (isEnter) store.submitInput()
                    else store.typeDigit(label)
                  }}
                  style={{
                    background: isEnter ? theme.acc : isBack ? theme.card : theme.acc2,
                    color: isEnter ? theme.onacc : isBack ? theme.ink : '#fff',
                  }}
                >
                  {label}
                </button>
              )
            })}
          </div>
        )}

        {q.mode === 'match' && <div className="match-hint">{T.matchHint}</div>}
      </div>

      {state.fb === 'ok' && (
        <div className="overlay overlay--ok">
          <div className="overlay__card overlay__card--ok">
            <div className="overlay__ok">✓</div>
            <div className="overlay__title">{T.correct}</div>
            <div className="overlay__xp" style={{ color: theme.acc }}>
              +{state.lastXp} XP
            </div>
          </div>
        </div>
      )}

      {state.fb === 'bad' && (
        <div className="overlay overlay--bad">
          <div className="overlay__card overlay__card--bad">
            <div className="overlay__oops">{T.oops}</div>
            <div className="overlay__answer">
              {q.a} × {q.b} = {q.answer}
            </div>
            <div className="hint-dots">
              {hintDots.map((i) => (
                <div key={i} className="hint-dot" />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
